import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { KeyRound, Plus, Copy, Lock, Unlock, Trash2, Shield } from "lucide-react";

export const AccessCodesTab = ({
  accessCodes,
  recoveryPhrase,
  onCreateCode,
  onToggleCode,
  onDeleteCode,
  onSaveRecoveryPhrase,
  onCopy,
}) => {
  const [recoveryInput, setRecoveryInput] = useState('');
  const [recoveryEditing, setRecoveryEditing] = useState(false);

  const handleSavePhrase = async () => {
    const ok = await onSaveRecoveryPhrase(recoveryInput.trim());
    if (ok) setRecoveryEditing(false);
  };

  return (
    <>
      <Card className="border-border/50" data-testid="access-codes-section">
        <CardHeader className="py-3 flex flex-row items-center justify-between">
          <CardTitle className="font-mono text-base flex items-center gap-2">
            <KeyRound className="w-4 h-4 text-primary" />
            Access Codes
          </CardTitle>
          <Button
            size="sm"
            onClick={onCreateCode}
            className="gap-1"
            data-testid="create-code-btn"
          >
            <Plus className="w-3.5 h-3.5" />
            Create Code
          </Button>
        </CardHeader>
        <CardContent className="p-2">
          {accessCodes.length === 0 ? (
            <p className="text-center text-muted-foreground py-6 text-sm">
              No access codes yet. Create one to allow new users to register.
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[600px]">
                <thead>
                  <tr className="bg-muted/30 text-xs font-mono">
                    <th className="p-2 text-left">Code</th>
                    <th className="p-2 text-center">Uses</th>
                    <th className="p-2 text-center">Status</th>
                    <th className="p-2 text-left">Used By</th>
                    <th className="p-2 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {accessCodes.map((c) => (
                    <tr key={c.id} className="border-t border-border/30 text-sm">
                      <td className="p-2">
                        <div className="flex items-center gap-1">
                          <span className="font-mono font-bold text-primary">{c.code}</span>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-6 w-6 p-0"
                            onClick={() => onCopy(c.code)}
                          >
                            <Copy className="w-3 h-3 text-muted-foreground" />
                          </Button>
                        </div>
                      </td>
                      <td className="p-2 text-center font-mono">
                        {c.used_count || 0} / {c.max_uses || '∞'}
                      </td>
                      <td className="p-2 text-center">
                        <span
                          className={`px-2 py-0.5 rounded text-xs font-mono ${
                            c.is_active
                              ? 'bg-green-500/20 text-green-500'
                              : 'bg-red-500/20 text-red-500'
                          }`}
                        >
                          {c.is_active ? 'Active' : 'Disabled'}
                        </span>
                      </td>
                      <td className="p-2 text-xs text-muted-foreground">
                        {(c.used_by || []).map((u) => u.email).join(', ') || '—'}
                      </td>
                      <td className="p-2 text-center">
                        <div className="flex gap-1 justify-center">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 px-2"
                            onClick={() => onToggleCode(c.id)}
                            data-testid={`toggle-code-${c.id}`}
                          >
                            {c.is_active ? (
                              <Lock className="w-3.5 h-3.5 text-red-500" />
                            ) : (
                              <Unlock className="w-3.5 h-3.5 text-green-500" />
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 px-2"
                            onClick={() => onDeleteCode(c.id)}
                            data-testid={`delete-code-${c.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5 text-red-500" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recovery Phrase Section */}
      <Card className="border-border/50" data-testid="recovery-phrase-section">
        <CardHeader className="py-3 flex flex-row items-center justify-between">
          <CardTitle className="font-mono text-base flex items-center gap-2">
            <Shield className="w-4 h-4 text-primary" />
            Password Recovery Phrase
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <p className="text-sm text-muted-foreground mb-3">
            Users can reset their password on the login screen by entering their email and this
            phrase. It is stored securely and can&apos;t be viewed again — you can only replace it.
          </p>
          {recoveryEditing ? (
            <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
              <Input
                value={recoveryInput}
                onChange={(e) => setRecoveryInput(e.target.value)}
                placeholder="Enter a new recovery phrase (min 6 characters)"
                className="font-mono flex-1"
                data-testid="recovery-phrase-input"
                autoFocus
              />
              <div className="flex gap-2">
                <Button size="sm" onClick={handleSavePhrase} data-testid="recovery-phrase-save">
                  Save
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => {
                    setRecoveryEditing(false);
                    setRecoveryInput('');
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
              {recoveryPhrase ? (
                <>
                  <div
                    className="font-mono text-sm bg-muted/30 px-3 py-2 rounded border border-border/50 flex-1"
                    data-testid="recovery-phrase-display"
                  >
                    A recovery phrase is set (hidden for security)
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setRecoveryInput('');
                        setRecoveryEditing(true);
                      }}
                      data-testid="recovery-phrase-edit"
                    >
                      Change
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <span className="text-sm text-muted-foreground">No recovery phrase set</span>
                  <Button
                    size="sm"
                    onClick={() => {
                      setRecoveryInput('');
                      setRecoveryEditing(true);
                    }}
                    data-testid="recovery-phrase-set"
                  >
                    <Plus className="w-3.5 h-3.5 mr-1" />
                    Set Phrase
                  </Button>
                </>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </>
  );
};
